import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import SearchBar from './SearchBar';
import SearchResults from './SearchResults';
import { useSearch } from '@/hooks/useSearch';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResultClick?: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose, onResultClick }) => {
  const { query, setQuery, results, isLoading } = useSearch();

  // キーボードショートカット処理
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K でモーダルを開く
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        // モーダルを開く処理は親コンポーネントで行うため、ここでは処理しない
      }
      
      // Escape でモーダルを閉じる
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // モーダル開いた時に検索バーにフォーカス
      setTimeout(() => {
        const input = document.querySelector('.search-input');
        if (input instanceof HTMLElement) {
          input.focus();
        }
      }, 100);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // モーダル外クリックで閉じる
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // 結果クリック時の処理
  const handleResultClick = () => {
    onResultClick?.();
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed inset-0 z-[100] flex items-start justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div 
        className="relative w-full max-w-2xl mt-20 bg-black/90 backdrop-blur-xl border border-white/10 rounded-none overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-lg font-medium text-white">サイト内検索</h2>
          <button
            onClick={onClose}
            className="p-2 text-white/50 hover:text-white transition-colors duration-300 rounded-none"
            aria-label="閉じる"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* 検索バー */}
        <div className="p-4 border-b border-white/10">
          <SearchBar
            onSearch={setQuery}
            placeholder="デモ、ブログ、チャレンジなどを検索..."
            initialValue={query}
            className="search-input"
          />
        </div>
        
        {/* 検索結果 */}
        <div className="p-4">
          <SearchResults
            results={results}
            query={query}
            isLoading={isLoading}
            onResultClick={handleResultClick}
            className="max-h-96"
          />
        </div>
        
        {/* キーボードショートカットのヒント */}
        <div className="p-4 border-t border-white/10 text-center">
          <p className="text-xs text-white/40">
            <kbd className="px-2 py-1 bg-white/[0.03] rounded-none">ESC</kbd> で閉じる
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SearchModal;